import React, { useState } from 'react';

import { ArrowDownIcon, ArrowRightIcon, ArrowUpIcon } from '$partials/icon';
import { Image } from '$partials/media';
import { Button, Input } from '$partials/ui';
import { Collapsible, CollapsibleContent, CollapsibleContextConsumer, CollapsibleHeading } from '$partials/base';
import { formatNumber, formatPrice } from '$utilities/format';
import { calculateStampDuty } from '$utilities/data';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
/**
 * @type {import('$types/components/partials/modules.d.ts').StampDutyCalculator}
 */
const StampDutyCalculator = ({
    image = {},
    brackets = [{
        leftCol: '0—£125,000',
        rightCol: '0%'
    },
    {
        leftCol: '£125,001—£250,000',
        rightCol: '2%'
    },
    {
        leftCol: '£250,001—£925,000',
        rightCol: '5%'
    },
    {
        leftCol: '£925,001—£1.5 million',
        rightCol: '10%'
    },
    {
        leftCol: 'Above £1.5 million',
        rightCol: '12%'
    }],
    footnote
}) => {
    const [ inputValue, setInputValue ] = useState('');
    const [ checkedValues, setCheckedValues ] = useState({
        isNotResident: false,
        isFirstHome: false,
        isSecondHome: false
    });
    const [results, setResults] = useState( {
        stampDuty: formatPrice(0),
        effectiveRate: formatNumber(0),
        taxableSum: formatPrice(0),
    });

    return (
        <div className='col-12'>
            {/* Input */}
            <div className='col-12'>
                <Input
                    className='type-style-3'
                    label='Total Purchase Sum'
                    onChange={(e)=> {
                        setInputValue(formatNumber(e.currentTarget.value.replaceAll(',', '')));}
                    }
                    renderIcon={<h3 className='type-style-3'>£</h3>}
                    value={inputValue}/>
            </div>

            {/* Background section */}
            <div className='absolute trunk-y-bleed translate-yn-100 zn1'>
                <div className='bg-peach height-7'/>
            </div>
            <div className='bg-peach trunk-full-width pb7 md-pb13'>
                <div className='container'>
                    <div className='flex flex-col col-12 md-col-5 pt7'>
                        <div className='flex checkbox items-center relative'>
                            <input
                                checked={checkedValues.isNotResident}
                                id='isNotResident'
                                name='isNotResident'
                                onChange={() => setCheckedValues(prev => ({...checkedValues, isNotResident: !prev.isNotResident}))}
                                type='checkbox'
                                value='isNotResident'/>
                            <label
                                className='type-style-6'
                                htmlFor='isNotResident'>The buyer is a non-UK resident</label>
                        </div>
                        <div className='flex checkbox items-center relative'>
                            <input
                                checked={checkedValues.isFirstHome}
                                id='isFirstHome'
                                name='isFirstHome'
                                onChange={() => setCheckedValues(prev => ({...checkedValues, isFirstHome: !prev.isFirstHome}))}
                                type='checkbox'
                                value='isFirstHome'/>
                            <label
                                className='type-style-6'
                                htmlFor='isFirstHome'>Property is a first home</label>
                        </div>
                        <div className='flex checkbox items-center relative'>
                            <input
                                checked={checkedValues.isSecondHome}
                                id='isSecondHome'
                                name='isSecondHome'
                                onChange={() => setCheckedValues(prev => ({...checkedValues, isSecondHome: !prev.isSecondHome}))}
                                type='checkbox'
                                value='isSecondHome'/>
                            <label
                                className='type-style-6'
                                htmlFor='isSecondHome'>Property is a buy to let or second home*</label>
                        </div>
                        <div className='ml7'>
                            <p
                                className='type-style-7'>
                                    {'*Buy-to-let/second home rates are 5 percentage points above the current SDLT (Stamp Duty Land Tax). You may be eligible for claiming this back or not paying this at all. Please gain independent advice or ask us to refer you to a suitable advisor.'}
                            </p>
                        </div>
                    </div>
                    <div className=' col-12 flex justify-between'>
                        <div className='col-12 md-col-6 flex flex-col pt7'>
                            {/* CTA button */}
                            <Button
                                as = 'button'
                                className = 'bg-black white type-style-5'
                                look = 'fill'
                                onClick={() => {
                                    const {totalDuty, taxableSum} = calculateStampDuty({
                                        priceGbp: inputValue.replaceAll(',', '')
                                    }, checkedValues);
                                    setResults({
                                        stampDuty: formatPrice(totalDuty),
                                        effectiveRate: inputValue ? formatNumber(totalDuty/(inputValue.replaceAll(',', '')) * 100, {maximumFractionDigits: 2}) : formatNumber(0),
                                        taxableSum: formatPrice(taxableSum)
                                    });
                                }}
                                renderIcon = {<ArrowRightIcon/>}>
                                View Results
                            </Button>

                            {/* Results */}
                            <div className='flex flex-col sm-flex-row col-12 mt7'>
                                <div className='border flex-grow px6 pt5 pb7'>
                                    <p className='type-style-5 py3'>Stamp duty to pay</p>
                                    <h3 className='type-style-3'>{results.stampDuty}</h3>
                                </div>
                                <div className='border-left border-right border-bottom sm-border-0 sm-border-top sm-border-bottom sm-border-right flex-grow px6 pt5 pb7'>
                                    <p className='type-style-5 py3'>Effective Rate</p>
                                    <h3 className='type-style-3'>{results.effectiveRate} %</h3>
                                </div>
                                <div className='border-left border-right border-bottom sm-border-0 sm-border-top sm-border-bottom sm-border-right flex-grow px6 pt5 pb7'>
                                    <p className='type-style-5 py3'>Taxable Sum</p>
                                    <h3 className='type-style-3'>{results.taxableSum}</h3>
                                </div>
                            </div>

                            {/* Accordion */}
                            <Collapsible
                                className='mt5'
                                id='stamp-duty-calculator'
                                isOpened={false}>
                                <CollapsibleHeading className='form-field-reset width-100'>
                                    <CollapsibleContextConsumer>
                                        {({ isOpened }) => (
                                            <div
                                                className={`inline-flex width-100 justify-between py5 md-py5 items-center bg-none type-style-5 cursor-pointer black${!isOpened ? ' border-bottom' : ''}`}>
                                                View brackets
                                                <span
                                                    className='font-0 line-height-0 bg-white black button border-radius-100px p3 md-p5'>
                                                    {isOpened ? <ArrowUpIcon/> : <ArrowDownIcon/>}
                                                </span>
                                            </div>
                                        )}
                                    </CollapsibleContextConsumer>
                                </CollapsibleHeading>
                                <CollapsibleContent className='clearfix'>
                                    {brackets.map((bracket, idx) => {
                                        return (
                                            <div
                                                className='col-12 flex justify-between mb3'
                                                key={`stamp-duty-bracket-${idx}`}>
                                                <p className='type-style-6'>{bracket.leftCol}</p>
                                                <p className='type-style-6'>{bracket.rightCol}</p>
                                            </div>
                                        );
                                    })}

                                </CollapsibleContent>
                            </Collapsible>

                            {/* Footnote */}
                            {footnote && (
                                <div className='mt9 col-8'>
                                    <p className='type-style-6'>{footnote}</p>
                                </div>
                            )}
                        </div>
                        <div className='hide md-show col-4'>
                            <Image {...image} />
                        </div>
                    </div>
                </div>
            </div>
            <div className='absolute trunk-y-bleed zn1'>
                <div className='bg-peach height-7'/>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default StampDutyCalculator;
